import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import antiInflammatoryBackground from '../../images/anti-inflammatory.png';
import antiInflammatoryFoodsImage from '../../images/anti-inflammatory-foods.webp';
import antiInflammatoryDietImage from '../../images/anti-inflammatory-diet.png';
import afmNutritionScienceImage from '../../images/afm_nutrition_science.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/anti-inflammatory';
const PAGE_TITLE = 'Anti-Inflammatory Diet Guide: Reduce Inflammation - Create A Dish';
const PAGE_DESCRIPTION = 'Focuses on foods that reduce chronic inflammation, such as fatty fish, berries, turmeric, and leafy greens, to support overall immune function.';
const PAGE_IMAGE = 'https://createadish.com/images/anti-inflammatory-og.png';

const AntiInflammatory: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="anti-inflammatory diet, reduce inflammation, immune health, wellness, healing foods, chronic inflammation" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Anti-Inflammatory Diet Guide: Reduce Inflammation",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "ImmuneHealth, Wellness, Healing"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${antiInflammatoryBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Anti-Inflammatory Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Reduce Inflammation</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Anti-Inflammatory Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Anti-Inflammatory Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#ImmuneHealth', '#Wellness', '#Healing'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Anti-Inflammatory Diet, which focuses on foods that reduce chronic inflammation, such as fatty fish, berries, turmeric, and leafy greens, to support overall immune function and wellness. This approach emphasizes whole, nutrient-dense foods while limiting processed items to combat conditions linked to inflammation.
          </Typography>
          <Box component="img" src={antiInflammatoryFoodsImage} alt="Anti-inflammatory foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The concept of an anti-inflammatory diet has roots in traditional eating patterns, such as the Mediterranean diet, which has been studied since the 1950s for its health benefits. Modern research on inflammation and nutrition gained traction in the 1990s, with studies linking diets high in processed foods to chronic inflammation. The term "anti-inflammatory diet" popularized in the early 2000s through works like Dr. Andrew Weil's books.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The anti-inflammatory diet prioritizes whole, unprocessed foods rich in antioxidants, omega-3s, and polyphenols while avoiding pro-inflammatory items.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fatty Fish</strong>: Salmon, mackerel, sardines for omega-3 fatty acids.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Berries and Fruits</strong>: Blueberries, strawberries, cherries for antioxidants.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Spices and Herbs</strong>: Turmeric (with black pepper), ginger, garlic.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Leafy Greens</strong>: Spinach, kale, broccoli, tomatoes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Nuts and Seeds</strong>: Walnuts, almonds, flaxseeds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Oils</strong>: Olive oil, avocado oil.</Typography>
            </Box>
            <Box component="img" src={antiInflammatoryDietImage} alt="Anti-inflammatory diet" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              This diet can lower chronic inflammation, reducing risks for heart disease, diabetes, arthritis, and certain cancers. It supports immune function, boosts mental health, aids weight management, and may improve cognitive function and longevity.
            </Typography>
            <Box component="img" src={afmNutritionScienceImage} alt="Anti-inflammatory nutrition science" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Anti-Inflammatory Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Small</strong>: Incorporate one anti-inflammatory food daily, like adding turmeric to meals.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Meal Prep</strong>: Prepare batches of veggies and proteins for easy access.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, such as "anti-inflammatory berry smoothie."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Monitor Progress</strong>: Track symptoms like energy or joint pain.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AntiInflammatory;
